/**
 * DEV-ONLY battle placeholders (owner: combat-builder). Draws labelled battle sprites (48 wide, centred
 * in the spec's frame) with a
 * real 4–8 frame idle / attack / skill / guard / hit cycle, and a 640x360 dusty-road-at-golden-hour
 * background, so the battle is readable before the art lands. Frame names follow the convention in
 * docs/asset-requests/combat-builder.md (`<anim>-<n>`), so the final atlas replaces these textures
 * without code changes. Never shipped: real art comes from assets-src/ via the asset pipeline.
 */
import Phaser from 'phaser';
import { GAME_H, GAME_W } from '@/core/config';
import type { BattleAnimName, BattleAnimSpec, BattleSpriteSpec } from './types';

export const BATTLE_ANIMS: readonly BattleAnimName[] = ['idle', 'attack', 'skill', 'guard', 'hit'];

export function battleFrameName(strip: string, n: number): string { return `${strip}-${n}`; }
export function battleAnimKey(texture: string, anim: string): string { return `${texture}:${anim}`; }

type Ctx = CanvasRenderingContext2D;

function rect(ctx: Ctx, x: number, y: number, w: number, h: number, c: string): void { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h)); }

function darken(hex: string, f: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.floor(((n >> 16) & 255) * f), g = Math.floor(((n >> 8) & 255) * f), b = Math.floor((n & 255) * f);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

interface Pose { lean: number; bob: number; sword: 'rest' | 'raise' | 'swing' | 'thrust' | 'guard'; reach: number; flinch: number; flash: boolean; lines: number }

function poseFor(anim: BattleAnimName, n: number, total: number): Pose {
  const p: Pose = { lean: 0, bob: 0, sword: 'rest', reach: 0, flinch: 0, flash: false, lines: 0 };
  const t = total <= 1 ? 0 : n / (total - 1);
  switch (anim) {
    case 'idle': p.bob = n === 1 || n === 2 ? 1 : 0; break;
    case 'attack': // wind-up (raise) → swing → recover
      if (t < 0.34) { p.sword = 'raise'; p.lean = -2; }
      else if (t < 0.7) { p.sword = 'swing'; p.lean = 3; p.reach = 4; p.lines = 2; }
      else { p.sword = 'rest'; p.lean = 1; }
      break;
    case 'skill': // crouch → thrust deep → hold → recover
      if (t < 0.25) { p.sword = 'raise'; p.lean = -3; p.bob = 2; }
      else if (t < 0.65) { p.sword = 'thrust'; p.lean = 5; p.reach = 8 + Math.round(t * 4); p.lines = 3; p.bob = 3; }
      else { p.sword = 'rest'; p.lean = 2; p.bob = 1; }
      break;
    case 'guard': p.sword = 'guard'; p.lean = -1; p.bob = n % 2; break;
    case 'hit': p.flinch = n < 2 ? 4 : 2; p.lean = -4; p.flash = n === 0; p.sword = 'rest'; break;
  }
  return p;
}

/** Draws one 48x64 frame facing right at (ox, oy); feet on the bottom row. */
function drawFighter(ctx: Ctx, ox: number, oy: number, pose: Pose, pal: BattleSpriteSpec['dev'], label: string): void {
  const robe = pal.robe, robeDark = darken(pal.robe, 0.65), outline = darken(pal.robe, 0.3);
  const cx = ox + 20 + pose.lean - pose.flinch;
  const top = oy + 8 + pose.bob;
  // shadow
  ctx.fillStyle = 'rgba(20,18,26,0.35)'; ctx.fillRect(ox + 8, oy + 60, 32, 3);
  // legs
  rect(ctx, cx - 7, oy + 46, 6, 14, robeDark); rect(ctx, cx + 1, oy + 46, 6, 14, robeDark);
  rect(ctx, cx - 8, oy + 58, 8, 3, outline); rect(ctx, cx, oy + 58, 8, 3, outline);
  // robe body
  rect(ctx, cx - 9, top + 18, 18, 30, robe);
  rect(ctx, cx - 10, top + 18, 1, 30, outline); rect(ctx, cx + 9, top + 18, 1, 30, outline);
  rect(ctx, cx - 9, top + 30, 18, 3, pal.sash); // sash
  rect(ctx, cx - 9, top + 47, 18, 1, outline);
  // arms
  rect(ctx, cx - 12, top + 20, 4, 14, robe); // back arm
  // head
  rect(ctx, cx - 6, top + 4, 13, 14, pal.skin);
  rect(ctx, cx - 7, top + 4, 1, 14, outline); rect(ctx, cx + 7, top + 4, 1, 14, outline);
  rect(ctx, cx - 7, top + 2, 15, 4, pal.hair); rect(ctx, cx - 2, top - 1, 6, 4, pal.hair); // hair + bun
  rect(ctx, cx + 3, top + 9, 2, 2, '#1a1210'); // eye (facing right)
  if (pal.beard) { rect(ctx, cx - 3, top + 15, 9, 8, pal.beard); rect(ctx, cx - 1, top + 22, 5, 4, pal.beard); }
  // sword arm + blade
  const blade = pal.blade, hilt = '#5a3a1e';
  switch (pose.sword) {
    case 'rest':
      rect(ctx, cx + 8, top + 22, 4, 12, robe);
      rect(ctx, cx + 10, top + 32, 3, 4, hilt); rect(ctx, cx + 11, top + 36, 2, 16, blade);
      break;
    case 'raise':
      rect(ctx, cx + 8, top + 14, 4, 10, robe);
      rect(ctx, cx + 9, top + 10, 3, 4, hilt); rect(ctx, cx + 10, top - 8, 2, 18, blade);
      break;
    case 'swing':
      rect(ctx, cx + 8, top + 22, 8, 4, robe);
      rect(ctx, cx + 16, top + 22, 4, 3, hilt); rect(ctx, cx + 20, top + 20 - pose.reach / 2, 16 + pose.reach, 2, blade);
      break;
    case 'thrust':
      rect(ctx, cx + 8, top + 26, 10, 4, robe);
      rect(ctx, cx + 18, top + 26, 4, 3, hilt); rect(ctx, cx + 22, top + 27, 14 + pose.reach, 2, blade);
      break;
    case 'guard':
      rect(ctx, cx + 8, top + 20, 6, 4, robe);
      rect(ctx, cx + 13, top + 36, 3, 4, hilt); rect(ctx, cx + 14, top + 8, 2, 28, blade);
      break;
  }
  // motion lines
  if (pose.lines > 0) { ctx.fillStyle = 'rgba(255,255,255,0.55)'; for (let i = 0; i < pose.lines; i++) ctx.fillRect(cx + 24 + i * 4, top + 14 + i * 6, 8, 1); }
  if (pose.flash) { ctx.fillStyle = 'rgba(255,120,90,0.35)'; ctx.fillRect(cx - 12, top, 28, 48); }
  // tiny label so critics can tell the placeholder from art
  ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(ox + 1, oy + 1, label.length * 4 + 2, 6);
  drawTinyLabel(ctx, label, ox + 2, oy + 2);
}

// 3x5 pixel glyphs for A-Z (dev labels only)
const GLYPHS: Record<string, string[]> = {
  J: ['001', '001', '001', '101', '010'], Z: ['111', '001', '010', '100', '111'], C: ['011', '100', '100', '100', '011'],
  B: ['110', '101', '110', '101', '110'], D: ['110', '101', '101', '101', '110'], E: ['111', '100', '110', '100', '111'], V: ['101', '101', '101', '101', '010'],
};
function drawTinyLabel(ctx: Ctx, text: string, x: number, y: number): void {
  ctx.fillStyle = '#ffffff';
  let cx = x;
  for (const ch of text) { const g = GLYPHS[ch]; if (g) for (let r = 0; r < 5; r++) for (let c = 0; c < 3; c++) if (g[r]?.[c] === '1') ctx.fillRect(cx + c, y + r, 1, 1); cx += 4; }
}

/** Anim names of a spec: the five canonical ones first, then any extra strips (observe, praise, …). */
export function battleAnimNames(spec: BattleSpriteSpec): string[] {
  const extras = Object.keys(spec.anims).filter((k) => !(BATTLE_ANIMS as readonly string[]).includes(k));
  return [...BATTLE_ANIMS, ...extras];
}

/**
 * Makes sure `spec.texture` exists and registers the global animations `<texture>:<anim>` for every
 * entry of `spec.anims`. Real art: the pipeline's atlas (`public/assets/manifest.json` group `battle`,
 * 80x64 frames named `<strip>-<n>`; an anim's `source` picks a differently named strip), or a plain
 * spritesheet whose numeric frames are laid out in BATTLE_ANIMS order (idle, attack, skill, guard, hit).
 * Missing strips simply register no animation — `BattleScene.playAnim` falls back to a canonical one.
 */
export function ensureBattleSprite(scene: Phaser.Scene, spec: BattleSpriteSpec, label = 'DEV'): void {
  const key = spec.texture;
  if (!scene.textures.exists(key)) drawDevSheet(scene, spec, label);
  const tex = scene.textures.get(key);
  const named = tex.has(battleFrameName('idle', 0));
  let cursor = 0;
  for (const anim of battleAnimNames(spec)) {
    const a = spec.anims[anim];
    if (!a) continue;
    const animKey = battleAnimKey(key, anim);
    const frames: Phaser.Types.Animations.AnimationFrame[] = [];
    const strip = a.source ?? anim;
    const canonical = (BATTLE_ANIMS as readonly string[]).includes(anim);
    for (let n = 0; n < a.frames; n++) {
      if (named) { const name = battleFrameName(strip, n); if (tex.has(name)) frames.push({ key, frame: name }); }
      else if (canonical) frames.push({ key, frame: cursor + n });
    }
    if (canonical) cursor += a.frames;
    if (scene.anims.exists(animKey) || frames.length === 0) continue;
    scene.anims.create({ key: animKey, frames, frameRate: a.fps, repeat: a.loop ? -1 : 0 });
  }
}

/** Frame names an anim needs from its atlas (`<source ?? anim>-<n>`), for validation tooling/tests. */
export function battleAnimFrameNames(anim: string, a: BattleAnimSpec): string[] {
  return Array.from({ length: a.frames }, (_, n) => battleFrameName(a.source ?? anim, n));
}

function drawDevSheet(scene: Phaser.Scene, spec: BattleSpriteSpec, label: string): void {
  const { frameW, frameH } = spec;
  const maxFrames = Math.max(...BATTLE_ANIMS.map((a) => spec.anims[a].frames));
  const canvas = scene.textures.createCanvas(spec.texture, frameW * maxFrames, frameH * BATTLE_ANIMS.length);
  if (!canvas) return;
  const ctx = canvas.getContext();
  ctx.imageSmoothingEnabled = false;
  const pad = Math.max(0, Math.floor((frameW - 48) / 2)); // the figure is drawn 48 wide; centre it in wider frames
  BATTLE_ANIMS.forEach((anim, row) => {
    const total = spec.anims[anim].frames;
    for (let n = 0; n < total; n++) {
      const ox = n * frameW, oy = row * frameH;
      ctx.save();
      if (spec.facing === 'left') { ctx.translate(ox + frameW - pad, oy); ctx.scale(-1, 1); drawFighter(ctx, 0, 0, poseFor(anim, n, total), spec.dev, ''); ctx.restore(); drawTinyLabel(ctx, label, ox + 2, oy + 2); }
      else { drawFighter(ctx, ox + pad, oy, poseFor(anim, n, total), spec.dev, label); ctx.restore(); }
      canvas.add(battleFrameName(anim, n), 0, ox, oy, frameW, frameH);
    }
  });
  canvas.refresh();
}

/** Dusty road at golden hour, 640x360 (dev placeholder for env-memory-road-bg). Deterministic. */
export function ensureBattleBackground(scene: Phaser.Scene, key: string): string {
  if (scene.textures.exists(key)) return key;
  const devKey = `${key}--dev`;
  if (scene.textures.exists(devKey)) return devKey;
  const canvas = scene.textures.createCanvas(devKey, GAME_W, GAME_H);
  if (!canvas) return devKey;
  const ctx = canvas.getContext();
  // sky bands (golden hour, no gradients per pixel grammar → 6 flat bands)
  const sky = ['#e9c37a', '#e6b46a', '#dfa15a', '#d38f4f', '#c47c46', '#a9693f'];
  sky.forEach((c, i) => rect(ctx, 0, i * 26, GAME_W, 26, c));
  // sun
  rect(ctx, 468, 44, 30, 30, '#fff1c2'); rect(ctx, 462, 50, 42, 18, '#fff1c2'); rect(ctx, 474, 38, 18, 42, '#fff1c2');
  // far hills
  rect(ctx, 0, 132, GAME_W, 40, '#8a6a4a');
  for (let x = 0; x < GAME_W; x += 40) { const h = 10 + ((x / 40) % 3) * 8; rect(ctx, x, 132 - h, 40, h, '#8a6a4a'); rect(ctx, x + 8, 132 - h + 3, 24, 2, '#a07e58'); }
  // tree line / grass
  rect(ctx, 0, 168, GAME_W, 24, '#6e7a3a');
  for (let x = 4; x < GAME_W; x += 28) { rect(ctx, x, 150, 10, 22, '#4b5a2c'); rect(ctx, x - 3, 146, 16, 8, '#5f7a34'); }
  // road (trapezoid drawn as bands)
  const roadTop = 192, roadBottom = GAME_H;
  for (let y = roadTop; y < roadBottom; y += 4) {
    const t = (y - roadTop) / (roadBottom - roadTop);
    const halfW = 120 + t * 260;
    const c = ((y / 4) % 2 === 0) ? '#d9b27a' : '#cfa56f';
    rect(ctx, GAME_W / 2 - halfW, y, halfW * 2, 4, c);
  }
  rect(ctx, 0, roadTop, GAME_W, 2, '#b58c5a');
  // ruts and pebbles (fixed pattern)
  for (let i = 0; i < 40; i++) { const x = (i * 97) % GAME_W; const y = 200 + ((i * 53) % 150); rect(ctx, x, y, 3, 2, '#b08a5c'); }
  // roadside pavilion (驿亭) on the right
  rect(ctx, 528, 96, 96, 6, '#7a4a2a'); rect(ctx, 520, 102, 112, 4, '#5a3a22'); rect(ctx, 536, 90, 80, 6, '#8c2a22');
  rect(ctx, 536, 106, 6, 84, '#6a4a2e'); rect(ctx, 610, 106, 6, 84, '#6a4a2e'); rect(ctx, 573, 106, 6, 84, '#6a4a2e');
  rect(ctx, 530, 186, 92, 6, '#8a6a4a');
  // small pack / bundle by the pavilion
  rect(ctx, 548, 176, 14, 10, '#8a5a32'); rect(ctx, 550, 172, 10, 4, '#e2c48c');
  // milestone post left
  rect(ctx, 60, 150, 8, 44, '#8a5a32'); rect(ctx, 58, 148, 12, 4, '#a07e58');
  // dust haze band
  ctx.fillStyle = 'rgba(255,220,160,0.18)'; ctx.fillRect(0, 176, GAME_W, 40);
  canvas.refresh();
  return devKey;
}
